import { useCallback, useMemo } from "react";
import { Form, useToast } from "@ir/ui";
import { UpdateConfigDeductionSlaConfigDTO } from "@src/domain/dto/config";
import type { ConfigDeductionSlaConfig } from "@src/domain/entities/config";
import { UpdateConfigDeductionSlaConfigUseCase } from "@src/application/useCases/config";
import { ConfigDeductionSlaConfigRepository } from "@src/infrastructure/repositories/config";
import { invalidateConfigListCacheScopes } from "@src/presentation/providers/config/configListCache";
import { useConfigDeductionSlaConfigs } from "@src/presentation/providers/config/ConfigDeductionSlaConfigsDataProvider";
import { ConfigAddEditModal } from "../utils/ConfigAddEditModal";
import {
  formatConfigRecordCount,
  useConfigAddEditModal,
} from "../utils/useConfigAddEditModal";
import { getConfigSaveErrorMessage } from "../utils/configSaveErrors";
import {
  ConfigDeductionSlaConfigAddEditForm,
  type DeductionSlaConfigAddEditFormValues,
} from "../utils/ConfigDeductionSlaConfigAddEditForm";
import { ConfigDeductionSlaConfigsTable } from "../utils/ConfigDeductionSlaConfigsTable";
import { ConfigWritableListLayout } from "../utils/ConfigWritableListLayout";

const EMPTY_FORM_VALUES: DeductionSlaConfigAddEditFormValues = {
  configKey: "",
  configValue: "",
  description: "",
};

export const ConfigDeductionSlaConfigsPage = () => {
  const {
    configs,
    loading,
    search,
    setSearch,
    total,
    setPage,
    refetch,
  } = useConfigDeductionSlaConfigs();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<DeductionSlaConfigAddEditFormValues>();

  const {
    modalMode,
    editingEntity: editingConfig,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal,
    openEditModal,
  } = useConfigAddEditModal<ConfigDeductionSlaConfig>();

  const repository = useMemo(() => new ConfigDeductionSlaConfigRepository(), []);
  const updateUseCase = useMemo(
    () => new UpdateConfigDeductionSlaConfigUseCase(repository),
    [repository],
  );

  const initialValues = useMemo((): DeductionSlaConfigAddEditFormValues => {
    if (modalMode === "edit" && editingConfig) {
      return {
        configKey: editingConfig.configKey,
        configValue: String(editingConfig.configValue),
        description: editingConfig.description ?? "",
      };
    }
    return EMPTY_FORM_VALUES;
  }, [editingConfig, modalMode]);

  const handleSubmit = useCallback(
    async (values: DeductionSlaConfigAddEditFormValues) => {
      if (modalMode !== "edit" || !editingConfig) {
        return;
      }

      setIsSaving(true);
      try {
        const configValue = Number(values.configValue.trim());
        if (!Number.isFinite(configValue)) {
          throw new Error("Config value must be a number.");
        }

        await updateUseCase.execute(
          new UpdateConfigDeductionSlaConfigDTO({
            id: editingConfig.id,
            configValue,
            // Preserve existing description (field is disabled; must not be cleared).
            description: editingConfig.description ?? null,
          }),
        );

        successToast({ content: "SLA config saved successfully" });
        invalidateConfigListCacheScopes(["sla-config"]);
        closeModal(form);
        refetch();
      } catch (err) {
        errorToast({
          content: getConfigSaveErrorMessage(
            err,
            "Failed to save SLA config. Please try again.",
            "Failed to save SLA config. Please try again later.",
          ),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      editingConfig,
      errorToast,
      form,
      modalMode,
      refetch,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  return (
    <>
      <ConfigWritableListLayout
        title="SLA Config"
        subtitle={formatConfigRecordCount(loading, total)}
        searchPlaceholder="Search SLA configs"
        searchByFields={["Config Key", "Description"]}
        search={search}
        setSearch={setSearch}
        loading={loading}
      >
        <ConfigDeductionSlaConfigsTable
          configs={configs}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          onEdit={openEditModal}
        />
      </ConfigWritableListLayout>

      <ConfigAddEditModal
        mode={modalMode}
        createTitle="Edit SLA Config"
        editTitle="Edit SLA Config"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={() => closeModal(form)}
      >
        <ConfigDeductionSlaConfigAddEditForm
          form={form}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </ConfigAddEditModal>
    </>
  );
};
